import { createStyles, rem } from '@mantine/core'

type StyleParams = {
  backgroundColor?: string
  useMobileLayout?: boolean
}

export default createStyles(
  (theme, { useMobileLayout, backgroundColor }: StyleParams = {}) => {
    return {
      resultItem: {
        padding: rem(12),
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            gap: 8,
            padding: rem(16),
          },
        }),
      },
      wrapper: {
        flexDirection: 'column-reverse',
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            height: 'min(800px,70vh)',
            flexDirection: 'row',
          },
        }),
      },
      content: {
        height: 'min(800px,70vh)',
        position: 'relative',
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            flex: 1,
            minWidth: 0,
            position: 'static',
          },
        }),
      },
      sidebar: {
        width: '100%',
        backgroundColor: theme.colors.neutral50,
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            width: rem(456),
            overflow: 'auto',
          },
          [theme.fn.largerThan(1500)]: {
            width: `max(${rem(456)},25%)`,
          },
        }),
      },
      sidebarExtend: {
        order: 3,
        paddingTop: rem(8),
        paddingInline: rem(16),
        paddingBottom: rem(12),
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            order: 1,
            paddingInline: rem(24),
            paddingTop: rem(40),
            paddingBottom: 0,
          },
        }),
      },
      sidebarSticky: {
        order: 1,
        zIndex: 1,
        paddingInline: rem(16),
        paddingBlock: rem(8),
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            order: 2,
            paddingTop: rem(16),
            paddingInline: rem(24),
            paddingBottom: rem(12),
          },
        }),
      },
      sidebarSearch: {
        ...(!useMobileLayout && {
          [theme.fn.largerThan('lg')]: { paddingBottom: rem(16) },
        }),
      },
      countTitle: {
        gap: rem(8),
        alignItems: 'center',
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: { flex: 1, minWidth: 0 },
        }),
        '.mantine-Select-item': {
          whiteSpace: 'nowrap',
          fontSize: rem(14),
          padding: '10px 16px',
        },
      },
      searchBtn: {
        ...(!useMobileLayout && {
          [theme.fn.largerThan('lg')]: { paddingTop: rem(12) },
        }),
      },
      sidebarList: {
        maxWidth: '100%',
        order: 2,
        display: 'flex',
        gap: rem(8),
        flexDirection: 'row',
        overflow: 'auto',
        paddingBlock: rem(8),
        paddingInline: rem(16),
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            order: 3,
            gap: 0,
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBlock: 0,
            paddingInline: 0,
          },
          [theme.fn.largerThan('lg')]: { flex: 1, minHeight: 0 },
        }),
      },
      controlBtns: {
        transform: 'translate(0, -50%)',
        backgroundColor: '#fff',
        borderRadius: rem(4),
        '.zoomBtn': {
          border: 'none',
          borderRadius: rem(4),
          borderTop: `1px solid ${theme.colors.gray100}`,
          borderInline: `1px solid ${theme.colors.gray100}`,
          '+ .zoomBtn': {
            borderRadius: 0,
          },
          '&:first-child': {
            borderRadius: `${rem(4)} ${rem(4)} 0 0`,
          },
          '&:last-child': {
            borderRadius: `0 0 ${rem(4)} ${rem(4)}`,
            borderBottom: `1px solid ${theme.colors.gray100}`,
          },
          '.zoomIcon': {
            width: '70%',
            height: '70%',
          },
          ...(!useMobileLayout && {
            [theme.fn.largerThan('sm')]: {
              width: rem(46),
              height: rem(46),
            },
          }),
        },
      },
      contentWrapper: {
        position: 'relative',
        maxHeight: '100%',
        display: 'flex',
        overflow: 'auto',
        flex: 1,
        background: 'white',
        transition: 'all .3s ease',
        [theme.fn.smallerThan('sm')]: {
          padding: '0 16px 16px',
          gap: rem(12),
        },
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            flexDirection: 'column',
            '&[data-opened="false"]': {
              transform: `translate(-50%, 0)`,
              opacity: 0,
            },
          },
        }),
      },
      searchItem: {
        width: '100%',
        background: 'white',
        '&[data-selected="true"]': {
          background: theme.colors.blue[0],
        },
        [theme.fn.smallerThan('sm')]: {
          border: `1px solid ${theme.colors.neutral[1]}`,
          borderRadius: rem(6),
        },
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            borderBottom: `1px solid ${theme.colors.neutral[1]}`,
          },
        }),
      },
      filterSelectRoot: { width: '120px' },
      filterSelectItem: { fontSize: '14px', padding: '8px 10px' },
      filterSelectInput: {
        height: 'auto !important',
        border: 0,
        paddingLeft: 0,
        paddingRight: rem(22),
        ':focus': {
          outline: 0,
          boxShadow: 'none',
        },
        [theme.fn.smallerThan('sm')]: {
          textAlign: 'right',
        },
      },
      filterSelectRightSection: {
        pointerEvents: 'none',
        width: rem(20),
      },
      filterSelectDropdown: {
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            transform: 'translate(-10px, 0)',
          },
        }),
      },
      foundListRoot: {
        display: 'flex',
        flexDirection: 'column',
        transition: 'all .3s ease',
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            position: 'absolute',
            zIndex: 2,
            top: rem(29),
            left: rem(10),
            maxHeight: `calc(100% - 53px)`,
            width: rem(312),
            alignItems: 'flex-start',
            '&[data-open="false"]': {
              pointerEvents: 'none',
            },
          },
        }),
        [theme.fn.smallerThan('sm')]: {
          border: 'none',
          transition: 'all .3s ease',
          background: 'white',
        },
      },
      foundListWrapper: {
        padding: `${rem(8)} ${rem(16)}`,
        [theme.fn.smallerThan('sm')]: {
          '&[data-open="false"]': {
            borderBottom: `1px solid ${theme.colors.neutral[1]}`,
          },
        },
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            padding: `${rem(8)} ${rem(12)}`,
            background: 'white',
            borderBottom: `1px solid ${theme.colors.neutral[1]}`,
            pointerEvents: 'initial',
            width: '100%',
            '&[data-open="false"]': {
              width: 'auto',
            },
          },
        }),
      },
      foundListInner: {
        [theme.fn.smallerThan('sm')]: {
          justifyContent: 'space-between',
        },
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            justifyContent: 'space-between',
            '&[data-open="false"]': {
              justifyContent: 'flex-start',
            },
          },
        }),
      },
      arrowBtn: {
        cursor: 'pointer',
        ...(!useMobileLayout && {
          [theme.fn.largerThan('sm')]: {
            marginTop: '3px',
          },
        }),
      },
      titleComp: {
        flex: 1,
        color: theme.colors.gray800,
        fontSize: rem(24),
        fontWeight: 700,
        lineHeight: rem(32),
        letterSpacing: rem(-0.8),
        textAlign: 'center',
        paddingTop: rem(48),
        paddingBottom: rem(16),
        // Breakpoint of show/hide title
        ...(!useMobileLayout && {
          [theme.fn.largerThan(1112)]: {
            textAlign: 'left',
            fontSize: rem(22),
            letterSpacing: rem(-0.6),
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
      },
      viewExpertBtn: {
        height: 'auto',
        paddingBlock: 0,
        paddingLeft: 0,
        paddingRight: 0,
        marginLeft: 'auto',
        borderRadius: rem(8),
        color: theme.colors.primary600,
        backgroundColor: 'transparent !important',
        '.mantine-Button-leftIcon': {
          marginRight: rem(12),
        },
        ...(!useMobileLayout && {
          [theme.fn.largerThan('md')]: {
            height: rem(48),
            paddingBlock: rem(12),
            paddingLeft: rem(12),
            paddingRight: rem(16),
            color: 'white',
            backgroundColor: `${
              backgroundColor || theme.colors.primary600
            } !important`, // TODO
            '&:hover': {
              backgroundColor: `${
                backgroundColor || theme.colors.primary500
              } !important`, // TODO
            },
          },
        }),
      },
    }
  },
)
